import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from '../apprenant/ApprenantPageShell';
import FormateurPageShell from '../formateur/FormateurPageShell';
import WalletActionsPanel from './WalletActionsPanel';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { fetchWalletDashboard } from '../../services/walletService';
import { formatMoney } from '../../utils/currency';

type Movement = {
  id: string;
  type: string;
  direction: string;
  amount: number;
  createdAt: string;
};

type Dashboard = {
  solde?: number;
  devise?: string;
  summary?: { available?: number };
  totaux?: { creditsAcademiquesCdf?: number; bonusParrainageCdf?: number };
  mouvementsRecents?: Movement[];
};

function movementLabel(type: string) {
  const map: Record<string, string> = {
    REFERRAL_BONUS_CREDIT: 'Bonus parrainage',
    FORMATION_WALLET_CREDIT: 'Crédit académique',
    FORMATION_PAYMENT_DEBIT: 'Paiement formation',
    RECHARGE_CREDIT: 'Recharge',
    TRANSFER_CREDIT: 'Transfert reçu',
    TRANSFER_DEBIT: 'Transfert envoyé',
    WITHDRAWAL_DEBIT: 'Retrait',
    FORMATEUR_REMUNERATION_CREDIT: 'Rémunération formateur',
  };
  return map[type] ?? type;
}

type Props = {
  portal: 'formateur' | 'partenaire' | 'ambassadeur';
  parrainagePath: string;
};

export default function PortalWalletPanel({ portal, parrainagePath }: Props) {
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setData((await fetchWalletDashboard()) as Dashboard);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le portefeuille.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useRegisterPortalRefresh(() => void load());

  const devise = data?.devise ?? 'CDF';
  const solde = data?.summary?.available ?? data?.solde ?? 0;
  const Shell = portal === 'formateur' || portal === 'ambassadeur' ? FormateurPageShell : ApprenantPageShell;

  const body = data ? (
    <div className="space-y-6">
      {actionMsg ? (
        <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800">{actionMsg}</p>
      ) : null}
      {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p> : null}

      <section className="grid gap-3 sm:grid-cols-2">
        <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase text-on-surface-variant">Solde disponible</p>
          <p className="mt-1 text-2xl font-black text-primary">{formatMoney(solde, devise)}</p>
        </article>
        <article className="rounded-xl border border-outline-variant/25 bg-white p-4 shadow-sm">
          <p className="text-xs font-bold uppercase text-on-surface-variant">Bonus parrainage</p>
          <p className="mt-1 text-xl font-black">{formatMoney(data.totaux?.bonusParrainageCdf ?? 0, devise)}</p>
        </article>
      </section>

      <Link
        to={parrainagePath}
        className="inline-flex items-center gap-2 rounded-lg border border-outline-variant/30 px-4 py-2 text-sm font-bold text-slate-700"
      >
        <LucideSymbolIcon name="groups" tone="inherit" size={18} />
        Parrainage
      </Link>

      <WalletActionsPanel
        walletSolde={solde}
        walletDevise={devise}
        showRecharge={false}
        showTransfer={false}
        onSuccess={(message) => {
          setActionMsg(message);
          void load();
        }}
        onError={(msg) => {
          setActionMsg(null);
          setError(msg);
        }}
      />

      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-white shadow-sm">
        <header className="border-b border-outline-variant/20 px-4 py-3">
          <h2 className="font-bold text-slate-900">Derniers mouvements</h2>
        </header>
        <ul className="divide-y divide-outline-variant/15">
          {(data.mouvementsRecents ?? []).length === 0 ? (
            <li className="px-4 py-8 text-center text-sm text-on-surface-variant">Aucun mouvement.</li>
          ) : (
            (data.mouvementsRecents ?? []).map((m) => (
              <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                <p className="font-semibold">{movementLabel(m.type)}</p>
                <p className={`font-black ${m.direction === 'CREDIT' ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {m.direction === 'CREDIT' ? '+' : '-'}
                  {formatMoney(m.amount, devise)}
                </p>
              </li>
            ))
          )}
        </ul>
      </section>
    </div>
  ) : null;

  return (
    <Shell
      title="Mon portefeuille"
      description="Recharges, retraits Mobile Money et transferts."
      sideCard={{
        label: 'Solde disponible',
        value: loading ? '…' : formatMoney(solde, devise),
        sideLinks: [{ label: 'Parrainage', to: parrainagePath, icon: 'groups' }],
      }}
      isLoading={loading}
      isError={Boolean(error) && !data}
      errorMessage={error ?? undefined}
      onRetry={() => void load()}
      loadingMessage="Chargement du portefeuille…"
    >
      {body}
    </Shell>
  );
}
